import isEqual from "lodash/isEqual";
import cloneDeep from "lodash/cloneDeep";

import { putRequestCase, postRequestCase, deleteRequestCase, getRequestCase } from "@/stores/caseStore/backendApi";
import { useCaseStore } from "@/stores/caseStore/0_caseStore";
import { withQuestionnaireSource } from "@/stores/caseStore/questionnaireSource";
import { useSnackbarStore } from "@/stores/snackbarStore";
import { encryptID } from "@/utils/security";
import * as d from "@/utils/dataUtils";

// model

export const defaultPoliceProsecution = {
  policeInvolved: null,
  prosecutionInvolved: null,

  policeStation: "",
  policeCaseNumber: "",

  policeContactPerson: null,
  policeSalutation: null,
  policeTitle: null,
  policeFirstName: "",
  policeLastName: "",
  policePhone: "",
  policeEmail: "",

  policeWarningClient: null,
  policeWarningOpponent: null,
  policeWarningClientDescription: "",
  policeWarningOpponentDescription: "",

  prosecutionStation: "",
  prosecutionCaseNumber: "",

  prosecutionContactPerson: null,
  prosecutionSalutation: null,
  prosecutionTitle: null,
  prosecutionFirstName: "",
  prosecutionLastName: "",
  prosecutionPhone: "",
  prosecutionEmail: "",

  remarks: "",
};

// helper

function encryptedCaseID(): string {
  const store = useCaseStore();
  return encryptID(store.caseData.id);
}

// CRUD

export async function loadPoliceProsecution(): Promise<void> {
  const store = useCaseStore();
  const response = await getRequestCase(`/protected/cases/${encryptedCaseID()}/police-prosecution`);
  const policeProsecution = preparePoliceProsecutionForLoad(response);
  store.policeProsecution = policeProsecution;
  store.lastSavedPoliceProsecution = cloneDeep(policeProsecution);
}

export async function addPoliceProsecution(): Promise<void> {
  const store = useCaseStore();
  const response = await postRequestCase(`/protected/cases/${encryptedCaseID()}/police-prosecution`);
  const policeProsecution = preparePoliceProsecutionForLoad(response);
  store.policeProsecution = policeProsecution;
  store.lastSavedPoliceProsecution = cloneDeep(policeProsecution);
}

export async function savePoliceProsecution(): Promise<void> {
  const store = useCaseStore();
  const snackbar = useSnackbarStore();

  try {
    const current = preparePoliceProsecutionForSave(store.policeProsecution);
    const previous = preparePoliceProsecutionForSave(store.lastSavedPoliceProsecution);

    if (isEqual(current, previous)) return;

    await putRequestCase(withQuestionnaireSource(`/protected/cases/${encryptedCaseID()}/police-prosecution/${current.id}`, store.questionnaireView), current);

    store.lastSavedPoliceProsecution = cloneDeep(store.policeProsecution);
  } catch (error: unknown) {
    const msg = error instanceof Error ? error.message : (error as any)?.message || "Fehler beim Speichern";
    snackbar.showSnackbar({ message: msg, type: "error" });
    throw error;
  }
}

export async function deletePoliceProsecution(idPoliceProsecution: number): Promise<void> {
  const store = useCaseStore();
  // Kundenfeedback 26.10.2026 (VIII): Löschen aus dem Fragebogen – ein
  // Eintrag mit Inhalt bleibt in der Akte, bis die Kanzlei entscheidet.
  await deleteRequestCase(withQuestionnaireSource(`/protected/cases/${encryptedCaseID()}/police-prosecution/${idPoliceProsecution}`, store.questionnaireView));
  store.policeProsecution = { ...defaultPoliceProsecution };
  store.lastSavedPoliceProsecution = { ...defaultPoliceProsecution };
}

// data helper

type PoliceProsecution = Record<string, any>;

export function preparePoliceProsecutionForLoad(data: PoliceProsecution): PoliceProsecution {
  return {
    ...data,

    policeInvolved: d.loadTristate(data.policeInvolved),
    prosecutionInvolved: d.loadTristate(data.prosecutionInvolved),

    policeContactPerson: d.loadTristate(data.policeContactPerson),
    policeSalutation: d.loadSelect(data.policeSalutation),
    policeTitle: d.loadSelect(data.policeTitle),

    policeWarningClient: d.loadTristate(data.policeWarningClient),
    policeWarningOpponent: d.loadTristate(data.policeWarningOpponent),

    prosecutionContactPerson: d.loadTristate(data.prosecutionContactPerson),
    prosecutionSalutation: d.loadSelect(data.prosecutionSalutation),
    prosecutionTitle: d.loadSelect(data.prosecutionTitle),
  };
}

export function preparePoliceProsecutionForSave(data: PoliceProsecution): PoliceProsecution {
  return {
    ...data,

    policeInvolved: d.saveTristate(data.policeInvolved),
    prosecutionInvolved: d.saveTristate(data.prosecutionInvolved),

    policeContactPerson: d.saveTristate(data.policeContactPerson),
    policeSalutation: d.saveSelect(data.policeSalutation),
    policeTitle: d.saveSelect(data.policeTitle),

    policeWarningClient: d.saveTristate(data.policeWarningClient),
    policeWarningOpponent: d.saveTristate(data.policeWarningOpponent),

    prosecutionContactPerson: d.saveTristate(data.prosecutionContactPerson),
    prosecutionSalutation: d.saveSelect(data.prosecutionSalutation),
    prosecutionTitle: d.saveSelect(data.prosecutionTitle),
  };
}
